import * as React from 'react';
import { useContext, useState } from 'react';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  UserGroupIcon,
  GlobeAltIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  RectangleStackIcon,
  ShoppingCartIcon,
  ArrowRightOnRectangleIcon,
  ServerStackIcon,
  CircleStackIcon,
  MegaphoneIcon,
} from '@heroicons/react/24/outline';
import { KeycloakContext } from '../../App';
import { Sidebar, type NavSection } from './Sidebar';
import { Topbar } from './Topbar';
import { ThemeToggle } from '../ui/theme-toggle';
import { ApiStatusIndicator, AppFooter } from '../features/ApiStatus';
import { PatchNotesButton } from '../features/PatchNotes';
import { Dropdown } from '../ui/dropdown';
import { useTheme } from '../../providers/ThemeProvider';
import { cn } from '../../lib/cn';

const STORAGE_KEY_COLLAPSED = 'edj-dashboard-admin-sidebar-collapsed';

function AdminLogo({ compact }: { compact?: boolean }) {
  const { resolvedTheme } = useTheme();
  const logoSrc = resolvedTheme === 'dark'
    ? '/img/edj-labs-logo-transparent-light.png'
    : '/img/edj-labs-logo-transparent-dark.png';
  if (compact) {
    return (
      <Link to="/admin" className="flex h-9 w-9 items-center justify-center" aria-label="EDJ-LABS Admin">
        <img src={logoSrc} alt="EDJ-LABS" className="h-8 w-8 object-contain" />
      </Link>
    );
  }
  return (
    <Link to="/admin" className="flex items-center" aria-label="EDJ-LABS Admin">
      <img src={logoSrc} alt="EDJ-LABS" className="h-8 w-auto max-w-[160px] object-contain" />
    </Link>
  );
}

function AdminUserMenu() {
  const ctx = useContext(KeycloakContext);
  const keycloak = ctx?.keycloak;
  const navigate = useNavigate();
  const username = (keycloak?.tokenParsed as any)?.preferred_username || (keycloak?.tokenParsed as any)?.email || 'Account';
  const firstName = (keycloak?.tokenParsed as any)?.given_name || '';
  const lastName = (keycloak?.tokenParsed as any)?.family_name || '';
  const displayName = [firstName, lastName].filter(Boolean).join(' ') || username;
  const initials = firstName && lastName
    ? (firstName[0] + lastName[0]).toUpperCase()
    : (displayName || 'A')
        .split(/[.\s_-]+/)
        .map((s: string) => s[0])
        .slice(0, 2)
        .join('')
        .toUpperCase();
  return (
    <Dropdown
      trigger={
        <button type="button" className="flex items-center gap-2 rounded-full border border-border bg-card p-0.5 pr-0.5 sm:pr-3 transition-colors hover:bg-accent" aria-label="Account menu">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">
            {initials}
          </span>
          <span className="hidden max-w-[160px] truncate text-xs font-medium text-foreground sm:inline">{displayName}</span>
        </button>
      }
      align="right"
      items={[
        { label: 'Back to dashboard', icon: <ArrowLeftIcon />, onSelect: () => navigate('/services') },
        { separator: true },
        { label: 'Sign out', icon: <ArrowRightOnRectangleIcon />, danger: true, onSelect: () => keycloak?.logout({ redirectUri: window.location.origin }) },
      ]}
    />
  );
}

export default function AdminLayout() {
  const ctx = useContext(KeycloakContext);
  const keycloak = ctx?.keycloak;
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return window.localStorage.getItem(STORAGE_KEY_COLLAPSED) === '1'; } catch { return false; }
  });
  React.useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY_COLLAPSED, collapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [collapsed]);

  React.useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  const isAdmin = keycloak?.tokenParsed?.realm_access?.roles?.includes('admin') || false;
  if (!isAdmin) return <Navigate to="/services" replace />;

  const sections: NavSection[] = [
    {
      label: 'Administration',
      items: [
        { name: 'Services', href: '/admin/services', icon: RectangleStackIcon },
        { name: 'Users', href: '/admin/users', icon: UserGroupIcon },
        { name: 'Domains', href: '/admin/domains', icon: GlobeAltIcon },
        { name: 'VPS', href: '/admin/vps', icon: ServerStackIcon },
        { name: 'Databases', href: '/admin/databases', icon: CircleStackIcon },
        { name: 'Orders', href: '/admin/orders', icon: ShoppingCartIcon },
        { name: 'Bills', href: '/admin/bills', icon: DocumentTextIcon },
        { name: 'Support', href: '/admin/support', icon: ChatBubbleLeftRightIcon },
        { name: 'Changelog', href: '/admin/changelog', icon: MegaphoneIcon },
      ],
    },
    {
      items: [
        { name: 'Back to dashboard', href: '/services', icon: ArrowLeftIcon },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Sidebar
        sections={sections}
        open={sidebarOpen}
        onOpenChange={setSidebarOpen}
        collapsed={collapsed}
        onCollapsedChange={setCollapsed}
        logoFull={<AdminLogo />}
        logoCompact={<AdminLogo compact />}
        footer={collapsed ? null : <AppFooter />}
      />
      <div className={cn('transition-[padding] duration-200', collapsed ? 'lg:pl-16' : 'lg:pl-64')}>
        <Topbar
          onOpenSidebar={() => setSidebarOpen(true)}
          right={
            <>
              <ApiStatusIndicator className="hidden sm:inline-flex" variant="pill" />
              <ApiStatusIndicator className="sm:hidden" variant="dot" />
              <PatchNotesButton />
              <ThemeToggle />
              <AdminUserMenu />
            </>
          }
        />
        <main className="px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          <div className="mx-auto w-full max-w-7xl space-y-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
